<script setup lang="ts">
  import type { IHeaderSection, IHeaderSectionChildItem } from '@/components/main-header/types'
  import type { IBlogEntry } from '@/server/api/blog-posts.get'
  import useHeaderDataLayer from '@/composables/data-layers/useHeaderDataLayer'

  const props = defineProps<{ activeSection?: IHeaderSection | null }>()

  const emit = defineEmits<{
    close: []
  }>()

  const { locale } = useI18n()
  const { pushHeaderSubcategoryCardClickDataLayer } = useHeaderDataLayer()

  const blogEntries = ref<IBlogEntry[]>([])
  async function getBlogEntries() {
    if (!props.activeSection?.hasBlogBlock || blogEntries.value?.length) {
      return
    }

    blogEntries.value = await $fetch<IBlogEntry[]>('/api/blog-posts', { query: { lang: locale.value } })
  }

  watch(
    () => props.activeSection,
    async () => {
      await getBlogEntries()
    }
  )

  onUnmounted(() => emit('close'))

  const route = useRoute()
  function onSectionItemClick(item: IHeaderSectionChildItem) {
    const [path, hash] = item.href.split('#')
    const isSamePage = path === route.path

    if (isSamePage && hash) {
      emit('close')
    }
  }

  function onBlogCardClick(entry: IBlogEntry) {
    if (!props.activeSection?.hasBlogBlock) {
      return
    }

    pushHeaderSubcategoryCardClickDataLayer(entry.title, entry.link.href)
  }
</script>
<template>
  <div
    v-if="activeSection"
    class="relative mx-auto grid w-full max-w-354 gap-x-10 px-24 pb-14 pt-8"
    :class="`grid-cols-${activeSection?.gridCols || 5}`"
  >
    <h3 class="sr-only">{{ $t('section', { label: activeSection.label }) }}</h3>
    <template
      v-for="section in activeSection.items"
      :key="section.label"
    >
      <LazyMainHeaderInfoCard
        v-if="section.isCard"
        :href="section.href"
        :title="section.label"
        :description="section.description"
        :bg-image-src="section.image?.src"
        class="col-span-2"
      />
      <div
        v-else
        :class="activeSection.hasBlogBlock ? 'col-span-2' : 'col-span-1'"
      >
        <a
          class="mb-4 block text-bt-blue-dark no-underline text-subtitle-1"
          :href="section.href"
        >
          {{ section.label }}
        </a>
        <div class="flex flex-col gap-4">
          <NuxtLink
            v-for="item in section.items"
            :key="item.label"
            :to="item.href"
            class="break-words text-bt-blue-dark no-underline transition-all text-body-1 hover:font-medium"
            external
            @click="onSectionItemClick(item)"
          >
            {{ item.label }}
          </NuxtLink>
        </div>
      </div>
      <template v-if="activeSection.hasBlogBlock">
        <LazyMainHeaderInfoCard
          v-for="entry in blogEntries"
          :key="entry.image"
          :href="entry.link.href"
          :title="entry.title"
          :description="entry.content"
          :bg-image-src="entry.image"
          class="col-span-2"
          @click="onBlogCardClick(entry)"
        />
      </template>
    </template>
  </div>
</template>
